import { ReactElement, useEffect } from 'react';
import { Address } from 'viem';

import { useConnection } from 'modules/auth/hooks/useConnection';
import successImage from 'modules/common/assets/stake-success.png';
import { SuccessDialogContent } from 'modules/common/components/SuccessDialogContent/SuccessDialogContent';
import { useDelegateMutation } from 'modules/delegate/actions/delegate';
import { DelegateForm } from 'modules/delegate/components/DelegateForm';
import { KnownDialogs, useDialog } from 'modules/dialogs';
import { useTranslation } from 'modules/i18n';
import { useGetPoolQuery } from 'modules/pool/actions/getPool';

import { translation } from './translation';

interface IDelegateContentProps {
  poolAddress: Address;
  formTitle?: string;
}

export function DelegateContent({
  formTitle,
  poolAddress,
}: IDelegateContentProps): ReactElement | null {
  const { t, keys } = useTranslation(translation);
  const { onClose } = useDialog(KnownDialogs.delegate);
  const { isSafeAccount } = useConnection();

  const { data: pool } = useGetPoolQuery({ address: poolAddress });
  const [delegate, { isLoading, data }] = useDelegateMutation();

  useEffect(() => {
    if (!isLoading && data && isSafeAccount) {
      onClose();
    }
  }, [isLoading, data, isSafeAccount, onClose]);

  if (!pool) {
    return null;
  }

  if (!isLoading && data && !isSafeAccount) {
    return (
      <SuccessDialogContent
        buttonLabel={t(keys.buttonText)}
        description={t(keys.doneDescription)}
        image={successImage}
        title={t(keys.done)}
        onClose={onClose}
      />
    );
  }

  return (
    <DelegateForm
      isSubmitLoading={isLoading}
      poolAddress={poolAddress}
      title={formTitle}
      onSubmit={({ amount }) =>
        delegate({
          amount,
          poolAddress,
        })
      }
    />
  );
}
